import * as React from 'react';

// utils
import AnalyticsService from 'modules/analytics-provider/analytics-provider.service';
import { translate } from 'components/translate-hoc';
import { generateTranslationsKeyById } from '@sitegen/common/utils/translations';
import {
    checkButtonAvailability,
    getConjunctionType,
    getRulesStatuses
} from './invitee.utils';
// constants
import {
    TEXT_KEYS,
    GET_ANALYTICS_INVITEE_INIT_DATA,
    GET_ANALYTICS_INVITEE_CLICK_BY_GAME, TEXT_KEYS as TEXT_KEYS_INVITEE
} from './invitee.constants';
import {
    INVITEE_PAGE_DESCRIPTION_KEY,
    INVITEE_PAGE_STATUS_KEY
} from '@sitegen/common/constants/translations';
import { CONJUNCTION_TYPES } from '@sitegen/common/classes/friends-referral-api';
// types
import { IInviteeProps } from './invitee.types';
// styles
import './invitee.styles.less';
// components
import SoftClosingBlock from '../soft-closing-block';
import { getModeClass } from 'utils/css';

export class Invitee extends React.Component<IInviteeProps> {
    public componentDidMount() {
        AnalyticsService.sendFlowEvent(GET_ANALYTICS_INVITEE_INIT_DATA());
    }

    public renderButton(): JSX.Element | null {
        const {
            dangerouslyTranslateWithHTML: translateKey,
            conversion: { rules },
            playerStats,
            shouldInheritStyles
        } = this.props;

        const isAvailable  = checkButtonAvailability(rules, playerStats);
        const basePageStyleClass = getModeClass('sitegen-fr-invitee', shouldInheritStyles);

        if (!isAvailable ) {
            return null;
        }

        return (
            <>
                <div className={`${basePageStyleClass}__message`}>
                    {translateKey(TEXT_KEYS.NOTE)}
                </div>
                <div
                    className={`${basePageStyleClass}__cta-button`}
                    onClick={this.buyAction}
                >
                    {translateKey(TEXT_KEYS.BUTTON_BUY)}
                </div>
            </>
        );
    }

    public renderInviteeRewardImage(): JSX.Element | null {
        const {
            dangerouslyTranslateWithHTML: translateKey,
            inviteeRewardImageSrc,
            shouldInheritStyles
        } = this.props;
        const basePageStyleClass = getModeClass('sitegen-fr-invitee', shouldInheritStyles);

        return <div className={`${basePageStyleClass}__image`}>
            {inviteeRewardImageSrc
                ? <div className={`${basePageStyleClass}__image-photo`}>
                    <img src={inviteeRewardImageSrc} alt=""/>
                </div>
                : null
            }

            <div className={`${basePageStyleClass}__image-title`}>
                {translateKey(TEXT_KEYS.REWARD_DESCRIPTION)}
            </div>
        </div>;
    }

    public getConjunctionText = (conjunctionType: CONJUNCTION_TYPES) => {
        return conjunctionType === CONJUNCTION_TYPES.AND
            ? TEXT_KEYS_INVITEE.CONJUNCTION_AND_TEXT
            : TEXT_KEYS_INVITEE.CONJUNCTION_OR_TEXT;
    };

    public renderRulesDescription = (): JSX.Element[] => {
        const {
            conversion: { rules, conjunction },
            dangerouslyTranslateWithHTML: translateKey,
            originalTranslateKey,
            playerStats,
            invitationInfo,
            dataFormat,
            shouldInheritStyles
        } = this.props;

        const conjunctionType = getConjunctionType(conjunction);
        const conjunctionText = this.getConjunctionText(conjunctionType);

        const statusesDescriptions = getRulesStatuses(rules, playerStats, invitationInfo, dataFormat);
        const basePageStyleClass = getModeClass('sitegen-fr-invitee', shouldInheritStyles);

        return statusesDescriptions.map((
            { id, statValue, paramThreshold, username, platform }, index) => (
            <React.Fragment key={index}>
                <div className={`${basePageStyleClass}__content-title`}>
                    {translateKey(
                        generateTranslationsKeyById(INVITEE_PAGE_STATUS_KEY, id),
                        {
                            statValue,
                            paramThreshold,
                            onPlatform: statValue ? originalTranslateKey(TEXT_KEYS.ON_PLATFORM, { platform }) : ''
                        }
                    )}
                </div>
                <div className={`${basePageStyleClass}__content-description`}>
                    {translateKey(
                        generateTranslationsKeyById(INVITEE_PAGE_DESCRIPTION_KEY, id),
                        { paramThreshold, username, platform }
                    )}
                </div>
                {index < statusesDescriptions.length - 1
                    ? <div className={`${basePageStyleClass}__content-conjunction`}>
                        <div className="conjunction-text">
                            {translateKey(conjunctionText)}
                        </div>
                    </div>
                    : null
                }
            </React.Fragment>
        ));
    };

    public renderSoftClosingBlock(): JSX.Element | null {
        const { softCloseMode } = this.props;
        return softCloseMode ? <SoftClosingBlock /> : null;
    }

    public render(): JSX.Element {
        const { dangerouslyTranslateWithHTML: translateKey, shouldInheritStyles } = this.props;
        const basePageStyleClass = getModeClass('sitegen-fr-invitee', shouldInheritStyles);

        return (
            <div className={basePageStyleClass}>
                <div className={`${basePageStyleClass}__caption`}>
                    {translateKey(TEXT_KEYS.CAPTION)}
                </div>
                <h2 className={`${basePageStyleClass}__title`}>
                    {translateKey(TEXT_KEYS.TITLE)}
                </h2>
                {this.renderSoftClosingBlock()}
                <div className={`${basePageStyleClass}__content`}>
                    {this.renderRulesDescription()}
                    {this.renderInviteeRewardImage()}
                    {this.renderButton()}
                </div>
            </div>
        );
    }

    private buyAction = () => {
        AnalyticsService.sendFlowEvent(GET_ANALYTICS_INVITEE_CLICK_BY_GAME());
    };
}

export const InviteeWithTranslations = translate()(Invitee);
